import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Input, Label } from "@activecollab/components";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import classNames from "classnames";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { editBillingPaymentMethod } from "~/store/actions/actions.billingPaymentMethod";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import Angie from "~/utils/angie";
import style from "./StripeCardForm.module.css";

const StripeCardForm = ({ onSubmit, onError, stripeFormId, isValidated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [isValidCardNumber, setIsValidCardNumber] = useState(false);
  const [isValidCardExpiration, setIsValidCardExpiration] = useState(false);
  const [isValidCVV, setIsValidCVV] = useState(false);
  const [cardHolder, setCardHolder] = useState("");

  const currentTheme = useSelector((state) => getRawPersonalizedConfigOption(state, "theme"));

  const onCardNumberChange = useCallback(
    (e) => {
      if (e.complete) {
        setIsValidCardNumber(true);
        return isValidated(isValidCardExpiration && isValidCVV && cardHolder);
      } else {
        return isValidated(false);
      }
    },
    [cardHolder, isValidCVV, isValidCardExpiration, isValidated]
  );
  const onCardExpirationChange = useCallback(
    (e) => {
      if (e.complete) {
        setIsValidCardExpiration(true);
        return isValidated(isValidCardNumber && isValidCVV && cardHolder);
      } else {
        return isValidated(false);
      }
    },
    [cardHolder, isValidCVV, isValidCardNumber, isValidated]
  );
  const onCardCvvChange = useCallback(
    (e) => {
      if (e.complete) {
        setIsValidCVV(true);
        return isValidated(isValidCardNumber && isValidCardExpiration && cardHolder);
      } else {
        return isValidated(false);
      }
    },
    [cardHolder, isValidCardExpiration, isValidCardNumber, isValidated]
  );
  const onCardHolderChange = useCallback(
    (e) => {
      if (e.target.value !== "") {
        setCardHolder(e.target.value);
        isValidated(isValidCardNumber && isValidCardExpiration && isValidCVV);
      } else {
        return isValidated(false);
      }
    },
    [isValidCVV, isValidCardExpiration, isValidCardNumber, isValidated]
  );

  const onSubmitCallback = useCallback(
    (e) => {
      e.preventDefault();
      if (!stripe || !elements) {
        return;
      }
      stripe
        .createToken(elements?.getElement(CardExpiryElement), {
          name: cardHolder,
        })
        .then((stripeResult) => {
          if (!stripeResult.error && stripeResult.token) {
            dispatch(
              editBillingPaymentMethod({
                card_type: stripeResult.token.card.brand,
                card_number: stripeResult.token.card.last4,
                expiration_month: stripeResult.token.card.exp_month,
                expiration_year: stripeResult.token.card.exp_year,
                token: stripeResult.token.id,
                card_id: stripeResult.token.card.id,
              })
            ).then(() => {
              onSubmit();
            });
          }
          if (stripeResult.error) {
            onError();
            Angie.logger.error("Error in payment details update", {
              error: stripeResult.error,
            });
            showErrorToast(t("Failed to update payment details"));
          }
        });
    },
    [cardHolder, dispatch, elements, onSubmit, onError, stripe, t]
  );

  const stripeElementStyles = useMemo(() => {
    return {
      style: {
        base: {
          color: currentTheme === "neon" ? "#f6f6f9" : "#303037",
          "::placeholder": {
            color: currentTheme === "neon" ? "#89898e" : "rgba(48,48,55,0.34)",
            fontWeight: 300,
            fontSize: 14,
          },
        },
      },
    };
  }, [currentTheme]);

  return (
    <form
      id={stripeFormId}
      onSubmit={onSubmitCallback}
      className="tw-w-auto sm:tw-w-[300px]"
    >
      <div className="form_input_container tw-mb-3">
        <Label>{t("Cardholder")}</Label>
        <Input
          className="tw-w-full tw-shrink-0 tw-border-gray-300 tw-p-2"
          onChange={onCardHolderChange}
        />
      </div>
      <div className="tw-mb-3 tw-border">
        <Label>{t("Card Number")}</Label>
        <CardNumberElement
          onChange={onCardNumberChange}
          options={stripeElementStyles}
          className={classNames(
            "tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-h-8 tw-p-2 stripeElements",
            style.stripeElements
          )}
        />
      </div>
      <div className="tw-flex tw-flex-1">
        <div className="tw-flex tw-flex-col tw-flex-1">
          <Label>{t("Expires")}</Label>
          <CardExpiryElement
            onChange={onCardExpirationChange}
            options={stripeElementStyles}
            className={classNames(
              "tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-h-8 tw-p-2 tw-mr-5 tw-align-middle tw-flex-1 stripeElements",
              style.stripeElements
            )}
          />
        </div>
        <div className="tw-flex tw-flex-col tw-flex-1">
          <Label>{t("Security Code")}</Label>
          <CardCvcElement
            onChange={onCardCvvChange}
            options={stripeElementStyles}
            className={classNames(
              "tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-h-8 tw-p-2 tw-flex-1 stripeElements",
              style.stripeElements
            )}
          />
        </div>
      </div>
    </form>
  );
};

StripeCardForm.propTypes = {
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  isValidated: PropTypes.func,
  stripeFormId: PropTypes.string,
};

StripeCardForm.defaultProps = {
  stripeFormId: "stripe_form",
};

export default StripeCardForm;
